import { Component } from '@angular/core';
import { Location } from '@angular/common';
import { TrainingService } from '../../../services/training.service';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingService } from '../../../services/loading.service';
import { TrainingFullModel } from '../../../models/training/training-full.model';
import { TrainingLocationTypeModel } from '../../../models/training/training-location-type.model';
import { TrainingTypeModel } from '../../../models/training/training-type.model';
import {
  calendarIcon,
  categoryIcon,
  bookmarkIcon,
  bookmarkOutlinedIcon,
} from '../../../app-icons';
import { TranslateService } from '@ngx-translate/core';
import { first, shareReplay, switchMap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, of } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { CookieService } from 'ngx-cookie-service';
import { AuthorizationService } from '../../../services/authorization.service';
import { PersonEventFlagService } from '../../../services/person-event-flag.service';
import { FileUtilService } from '../../../services/file-util.service';
import { EventMemberStatusModel } from 'src/app/models/events/event-member-status.model';
import { EventParticipationAnswerCreationModel } from 'src/app/models/event-participations/event-participation-answer-creation.model';
import { HttpStatusCode } from '@angular/common/http';
import { UserContextService } from 'src/app/services/user-context.service';
import { PageStateFlags } from 'src/app/constants/page-state.constants';
import { PageStateService } from 'src/app/services/page-state.service';

@Component({
  selector: 'app-training-info',
  templateUrl: './training-info.component.html',
  styleUrls: ['./training-info.component.scss'],
  standalone: false
})
export class TrainingInfoComponent {
  trainingId: string;
  TrainingLocationType = TrainingLocationTypeModel;
  TrainingType = TrainingTypeModel;
  training: TrainingFullModel | null = null;
  memberStatus: number | null = null;
  joinUrl: string | null = null;
  isOldTraining: boolean = false;
  isFromSubscribedSource: boolean = false;
  isCurrentUserTrainer: boolean = false;
  isCurrentUserCreator: boolean = false;
  isInitialLoad = true;
  isAdmin = false;
  isTrainingCatalogue = false;
  isFromCalendar = false;
  isBookmarked = false;
  isAddedToCalendar = false;
  isBookedUp = false;
  isDeployed = false;
  showDeclineInvitationModal = false;
  showAnswerAdditionalQuestionsModal = false;
  showShareIddNumberModal = false;

  showTrainingActionModal = false;
  trainingStep: 'idd' | 'calendar' = 'calendar';

  MemberStatus = EventMemberStatusModel;

  get locationType() {
    switch (this.training?.training.trainingLocation.type) {
      case TrainingLocationTypeModel.Address:
        return this.translateService.instant(
          'COMMON.FIELDS.EVENT-TYPES.FACE-TO-FACE'
        );
      case TrainingLocationTypeModel.Web:
        return this.translateService.instant(
          'COMMON.FIELDS.EVENT-TYPES.ONLINE'
        );
      case TrainingLocationTypeModel.GoToWebinar:
        return 'GoToWebinar';
      case TrainingLocationTypeModel.Webex:
        return 'Webex';
      default:
        return '';
    }
  }

  get trainingType() {
    switch (this.training?.training.trainingType) {
      case TrainingTypeModel.General:
        return this.translateService.instant(
          'COMMON.FIELDS.TRAINING-TYPES.GENERAL'
        );
      case TrainingTypeModel.Compliance:
        return this.translateService.instant(
          'COMMON.FIELDS.TRAINING-TYPES.COMPLIANCE'
        );
      case TrainingTypeModel.IDD:
        return this.translateService.instant(
          'COMMON.FIELDS.TRAINING-TYPES.IDD'
        );
      default:
        return '';
    }
  }

  get trainingGroups() {
    if (!this.training?.trainingGroups.length) {
      return '';
    } else {
      return this.training?.trainingGroups
        .map((group) => group.name)
        .join(', ');
    }
  }

  get durationString() {
    if (
      !this.training ||
      !this.training?.training ||
      typeof this.training?.training.iddTimeMinutes !== 'number' ||
      isNaN(this.training?.training.iddTimeMinutes)
    ) {
      return '00:00 h';
    }

    const hours = Math.floor(this.training?.training.iddTimeMinutes / 60);
    const remainingMinutes = this.training?.training.iddTimeMinutes % 60;
    const durationString = `${hours}:${
      remainingMinutes < 10 ? '0' : ''
    }${remainingMinutes} h`;

    return durationString;
  }

  get trainingCategories() {
    if (
      this.training &&
      this.training.trainingCategories &&
      this.training.trainingCategories.length
    ) {
      return this.training.trainingCategories
        .map((category) => category.name)
        .join(', ');
    } else {
      return null;
    }
  }

  get isExternalEvent() {
    if(!this.training) return false;
    return this.training.training.trainingLocation.type ===
      TrainingLocationTypeModel.ExternalEvent;
  }

  get showJoinUrl(): boolean {
    if(!this.training) return false;

    return (
      (
        this.training.training.trainingLocation.type !== TrainingLocationTypeModel.Address &&
        this.memberStatus === 1
      ) ||
      this.isExternalEvent
    );
  }

  constructor(
    private route: ActivatedRoute,
    private trainingService: TrainingService,
    private personEventFlagService: PersonEventFlagService,
    private loadingService: LoadingService,
    private router: Router,
    private toastrService: ToastrService,
    public translateService: TranslateService,
    private location: Location,
    private authorizationService: AuthorizationService,
    private sanitized: DomSanitizer,
    private cookieService: CookieService,
    private fileUtilService: FileUtilService,
    private userService: UserContextService,
    private pageStateService: PageStateService
  ) {
    this.isTrainingCatalogue = !!this.route.snapshot.params['categoryId'];
    this.isFromCalendar = !!this.route.snapshot.queryParams['isFromCalendar'];
    this.trainingId = this.route.snapshot.params['id'];
  }

  ngOnInit() {
    this.loadTrainingData();

    this.personEventFlagService.addPersonEventFlag(
      this.trainingId,
      'Opened'
    ).subscribe();
  }

  getCalendarIcon() {
    return this.sanitized.bypassSecurityTrustHtml(calendarIcon);
  }

  getCategoryIcon() {
    return this.sanitized.bypassSecurityTrustHtml(categoryIcon);
  }

  getBookmarkIcon() {
    return this.sanitized.bypassSecurityTrustHtml(bookmarkIcon);
  }

  getBookmarkOutlinedIcon() {
    return this.sanitized.bypassSecurityTrustHtml(bookmarkOutlinedIcon);
  }

  loadTrainingData(): void {
    const trainingData = this.loadingService
      .load(
        forkJoin({
          training: this.trainingService.getTraining(this.trainingId),
          trainingMember: this.trainingService.getTrainingMemberStatus(
            this.trainingId
          ),
          personEventFlags: this.personEventFlagService.getPersonEventFlags(
            this.trainingId
          ),
        })
      )
      .pipe(shareReplay(1));

    trainingData.subscribe(({ training, trainingMember, personEventFlags }) => {
      this.training = training;
      this.isFromSubscribedSource = !!training.isFromSubscribedSource;
      this.isCurrentUserTrainer = !!training.isCurrentUserTrainer;
      this.isCurrentUserCreator = !!training.isCurrentUserCreator;
      this.isBookedUp = this.training.isBookedUp;
      this.isDeployed = this.training.training.isDeployed;

      if(trainingMember) {
        this.memberStatus = trainingMember.status;
        this.joinUrl = trainingMember.joinUrl;
      }

      this.isBookmarked = !!personEventFlags.find(
        (flag) => flag.actionFlag == 1
      );
      this.isAddedToCalendar = !!personEventFlags.find(
        (flag) => flag.actionFlag == 2
      );

      const lastDate = new Date(
        this.training.dates[this.training.dates.length - 1].end
      );
      if (lastDate < new Date()) {
        this.isOldTraining = true;
      }

      this.runAutoActions();

      this.isInitialLoad = false;

      this.authorizationService
        .getCurrentPerson()
        .pipe(first())
        .subscribe((currentUser) => {
          if (
            currentUser.person.role.name == 'SuperAdmin' ||
            this.training?.trainers.find(
              (trainer) => trainer.id == currentUser.person.id
            )
          ) {
            this.isAdmin = true;
          }
        });
    });
  }

  private autoActionsExecuted = false;
  private runAutoActions(): void {
    if (this.autoActionsExecuted) return;

    const query = this.route.snapshot.queryParams;

    const autoWaitlist = query['auto-waitlist'];
    const autoAccept = query['autoaccept'];

    if (
      autoWaitlist &&
      this.memberStatus !== EventMemberStatusModel.Waiting &&
      !this.isOldTraining &&
      this.isBookedUp
    ) {
      this.autoActionsExecuted = true;
      this.onAddToQueueClick();
      return;
    }

    if (
      autoAccept &&
      this.memberStatus !== EventMemberStatusModel.Accepted &&
      !this.isOldTraining
    ) {
      this.autoActionsExecuted = true;
      this.onAcceptInvitationClick();
    }
  }

  goToExternalEvent(): void {
    const eventUrl = 
      this.training?.training.trainingLocation.suffix;

    if (!eventUrl) {
      return;
    }

    const url = eventUrl.startsWith('http')
      ? eventUrl
      : `https://${eventUrl}`;

    window.open(url, '_blank');
  }

  get externalEventUrl(): string {
    const url = 
      this.training?.training.trainingLocation.suffix;

    return url?.startsWith('http') 
      ? url 
      : `https://${url}`;
  }

  onAcceptInvitationClick(): void {
    if(!this.training) return;

    if(
      this.training.training.trainingLocation.type == TrainingLocationTypeModel.Address && 
       this.training.questions && this.training.questions.length !== 0
    ) {
      this.showAnswerAdditionalQuestionsModal = true;
    } else {
      this.acceptTraining(null);
    }
  }

  onAdditionalQuestionsAnswered(answers: EventParticipationAnswerCreationModel[]) {
    if(this.isBookedUp) {
      this.enqueueTraining(answers);
    } else {
      this.acceptTraining(answers);
    }
  }

  private acceptTraining(answers: EventParticipationAnswerCreationModel[] | null) {
    this.loadingService
    .load(this.trainingService.acceptTraining(this.trainingId, answers))
    .pipe(
      switchMap(() => {
        const expirationDate = new Date();
        expirationDate.setFullYear(expirationDate.getFullYear() + 1);

        this.cookieService.set(
          `training-${this.training?.training.key}`,
          'true',
          expirationDate,
          '/'
        );

        this.loadTrainingData();

        this.toastrService.success(
          this.translateService.instant(
            'MY-TRAININGS.OVERVIEW.ACCEPT-INVITATION-SUCCESSFUL'
          )
        );

        return this.isFromSubscribedSource && this.userService.currentPerson?.person.iddNumber
          ? this.trainingService.hasBrokerIddNumber(this.trainingId)
          : of(true);
      })
    )
    .subscribe({
      next: (hasIdd) => {
        this.showTrainingActionModal = true;
        this.trainingStep = hasIdd === false ? 'idd' : 'calendar';
      },
      error: (error) => {
        if (error.status === HttpStatusCode.Conflict) {
          this.toastrService.error(
            this.translateService.instant("MY-INVITATION.EVENT-BOOKED")
          );
          this.loadTrainingData();
        } else {
          this.toastrService.error(
            this.translateService.instant("COMMON.AN-ERROR-HAS-OCCURRED")
          );
        }
      },
    });
  }

  onAddToQueueClick(): void {
    if(!this.training) return;

    if(
      this.training.training.trainingLocation.type == TrainingLocationTypeModel.Address && 
       this.training.questions && this.training.questions.length !== 0
    ) {
      this.showAnswerAdditionalQuestionsModal = true;
    } else {
      this.enqueueTraining(null);
    }
  }

  private enqueueTraining(answers: EventParticipationAnswerCreationModel[] | null) {
     this.loadingService
      .load(
        this.trainingService.enqueueTraining(this.trainingId, answers)
      )
      .pipe(
        switchMap(() => {
          this.loadTrainingData();

          this.toastrService.success(
            this.translateService.instant(
              'MY-TRAININGS.TRAINING-QUEUE.ADDED-TO-QUEUE-SUCCESSFULLY'
            )
          );

          return this.isFromSubscribedSource && this.userService.currentPerson?.person.iddNumber
            ? this.trainingService.hasBrokerIddNumber(this.trainingId)
            : of(true);
        })
      )
      .subscribe({
        next: (hasIdd) => {
          if(!hasIdd) {
            this.showShareIddNumberModal = true;
          }
        },
        error: () =>
          this.toastrService.error(
            this.translateService.instant('COMMON.AN-ERROR-HAS-OCCURRED')
          ),
      });
  }

  onRemoveFromQueueClick(): void {
    this.loadingService
      .load(this.trainingService.dequeueTraining(this.trainingId))
      .subscribe({
        next: () => {
          this.loadTrainingData();

          this.toastrService.success(
            this.translateService.instant(
              'MY-TRAININGS.TRAINING-QUEUE.REMOVED-FROM-QUEUE-SUCCESSFULLY'
            )
          );
        },
        error: () =>
          this.toastrService.error(
            this.translateService.instant('COMMON.AN-ERROR-HAS-OCCURRED')
          ),
      });
  }


  onDeclineInvitationClick(): void {
    this.loadingService
      .load(this.trainingService.declineTraining(this.trainingId))
      .subscribe({
        next: () => {
          this.cookieService.delete(
            `training-${this.training?.training.key}`,
            '/'
          );

          this.loadTrainingData();
          this.toastrService.success(
            this.translateService.instant(
              'MY-TRAININGS.OVERVIEW.DECLINE-INVITATION-SUCCESSFUL'
            )
          );
        },
        error: () =>
          this.toastrService.error(
            this.translateService.instant('COMMON.AN-ERROR-HAS-OCCURRED')
          ),
      });
  }

  onDeclineTrainerInvitationClick(): void {
    this.showDeclineInvitationModal = true;
  }

  declineTrainerInvitation(): void {
    this.loadingService
      .load(this.trainingService.declineTrainerInvitation(this.trainingId))
      .subscribe({
        next: () => {
          this.loadTrainingData();

          this.toastrService.success(
            this.translateService.instant(
              'TRAINER-DETAILS.DECLINED-INVITATION-SUCCESSFULLY'
            )
          );
          
          this.showDeclineInvitationModal = false;
        },
        error: () =>
          this.toastrService.error(
            this.translateService.instant('COMMON.AN-ERROR-HAS-OCCURRED')
          ),
      });
  }

  navigateBack(): void {
    if (this.isTrainingCatalogue) {
      this.pageStateService.setFlag(
        PageStateFlags.RestoreTrainingCatalogueOverview
      );
      
      this.router.navigate([
        `/training-catalogue/${this.route.snapshot.params['categoryId']}`,
      ]);
    } 
    else if (this.isFromCalendar) {
      this.router.navigate(['/training-calendar']);
    } else {
      this.pageStateService.setFlag(
        PageStateFlags.RestoreMyTrainingsOverviewTable
      );

      this.router.navigate(['/my-trainings']);
    }
  }

  toggleBookmark() {
    if (this.isBookmarked) {
      this.loadingService
        .load(
          this.personEventFlagService.deletePersonEventFlag(
            this.trainingId,
            'Bookmark'
          )
        )
        .subscribe(() => {
          this.toastrService.success(
            this.translateService.instant(
              'TRAINING-CATALOGUE.UNBOOKMARK-SUCCESS'
            )
          );
          this.isBookmarked = false;
        });
    } else {
      this.loadingService
        .load(
          this.personEventFlagService.addPersonEventFlag(
            this.trainingId,
            'Bookmark'
          )
        )
        .subscribe(() => {
          this.toastrService.success(
            this.translateService.instant('TRAINING-CATALOGUE.BOOKMARK-SUCCESS')
          );
          this.isBookmarked = true;
        });
    }
  }

  addToCalendar() {
    this.loadingService
      .load(
        this.personEventFlagService.addPersonEventFlag(
          this.trainingId,
          'AddToCalendar'
        )
      )
      .subscribe(() => {
        this.toastrService.success(
          this.translateService.instant(
            'MY-TRAININGS.SAVED-TO-CALENDAR-SUCCESSFUL'
          )
        );
        this.isAddedToCalendar = true;
        this.onDownloadIcsClick();
      });
  }

  addedToCalendar() {
    this.isAddedToCalendar = true;
  }

  onAdminViewClick(): void {
    this.router.navigate(['training', this.trainingId]);
  }

  onDownloadIcsClick(): void {
    this.loadingService
      .load(this.trainingService.getIcs(this.trainingId))
      .subscribe({
        next: (ics) => this.fileUtilService.downloadFile(ics),
        error: () => this.toastrService.error(
          this.translateService.instant(
            "COMMON.AN-ERROR-HAS-OCCURRED"
          )
        ),
      });
  }
}
